import {
  useCallback,
  useEffect,
  useRef,
  useSyncExternalStore,
  type KeyboardEvent,
  type MouseEvent,
  type SetStateAction,
} from 'react';
import { checkStateOf, rangeTargets, setMany, type CheckState } from '@/lib/rangeSelect';

/**
 * Shift-click range selection for a list with per-row checkboxes.
 *
 * The page keeps owning its selection (a Set, a Map, a record of line
 * states): it says which ids are on screen, which are selected, and how to
 * set some ids to checked or unchecked. The hook keeps the anchor (the last
 * row clicked) and turns clicks into ranges. The anchor resets whenever the
 * ids on screen change (a filter, a sort, another page).
 *
 *   const sel = useRangeSelection({ visible: ids, isSelected: (id) => s.has(id), onApply: applyTo(setS) });
 *   <input type="checkbox" {...sel.checkboxProps(id)} />   // shift-click ranges
 *   <tr {...sel.rowProps(id)}>                              // whole-row click, optional
 *
 * Shift is read from the click itself, not from onChange: a click on a
 * checkbox's <label> reaches the box as a synthetic click that may not carry
 * the modifier, so the mousedown (or shift+Space keydown) that started it is
 * remembered too.
 */
export interface RangeSelectionOptions<K> {
  /** Ids in the order they're on screen right now. */
  visible: readonly K[];
  isSelected: (id: K) => boolean;
  /** Set every id in `ids` to `checked`. */
  onApply: (ids: K[], checked: boolean) => void;
  /** Rows that can't be selected: never toggled, skipped in ranges and select-all. */
  isDisabled?: (id: K) => boolean;
}

/** Elements inside a row that keep their own click. */
const INTERACTIVE =
  'a,button,input,select,textarea,label,summary,[role="button"],[role="link"],[role="menuitem"],[role="checkbox"],[role="combobox"],[contenteditable="true"],[data-row-click-ignore]';

/** True when a click on `row` came from (or through) something that handles its own clicks. */
export function isInteractiveClick(target: EventTarget | null, row: Element): boolean {
  if (!(target instanceof Element)) return true;
  // Portalled dialogs and menus bubble React events through the row.
  if (!row.contains(target)) return true;
  const hit = target.closest(INTERACTIVE);
  return !!hit && hit !== row && row.contains(hit);
}

function hasTextSelection(): boolean {
  const s = typeof window !== 'undefined' ? window.getSelection() : null;
  return !!s && !s.isCollapsed && s.toString().trim() !== '';
}

function clearTextSelection(): void {
  if (typeof window !== 'undefined') window.getSelection()?.removeAllRanges();
}

export function useRangeSelection<K extends string | number>({
  visible,
  isSelected,
  onApply,
  isDisabled,
}: RangeSelectionOptions<K>) {
  const anchor = useRef<K | null>(null);
  const shiftHeld = useRef(false);
  const latest = useRef({ visible, isSelected, onApply, isDisabled });
  latest.current = { visible, isSelected, onApply, isDisabled };

  const signature = visible.join('\u0001');
  useEffect(() => {
    anchor.current = null;
  }, [signature]);

  /** Set `id` (or the range anchor…id on shift) to `checked`. */
  const toggle = useCallback((id: K, checked: boolean, shift: boolean) => {
    const { visible: ids, onApply: apply, isDisabled: disabled } = latest.current;
    const targets = rangeTargets({ visible: ids, anchor: shift ? anchor.current : null, target: id, isDisabled: disabled });
    if (targets.length === 0) return;
    anchor.current = id;
    if (shift) clearTextSelection();
    if (targets.length > 1) markRangeHintSeen();
    apply(targets, checked);
  }, []);

  const onMouseDown = useCallback((e: MouseEvent<HTMLElement>) => {
    shiftHeld.current = e.shiftKey;
    // Shift+mousedown would otherwise extend the page's text selection.
    if (e.shiftKey && !(e.target instanceof HTMLElement && e.target.matches('input:not([type="checkbox"]),textarea,select'))) {
      e.preventDefault();
    }
  }, []);

  const checkboxProps = (id: K) => ({
    checked: latest.current.isSelected(id),
    disabled: latest.current.isDisabled?.(id) ?? false,
    // The click does the work; onChange only keeps React's controlled-input contract.
    onChange: noop,
    onClick: (e: MouseEvent<HTMLInputElement>) => {
      const shift = e.shiftKey || shiftHeld.current;
      shiftHeld.current = false;
      toggle(id, !latest.current.isSelected(id), shift);
    },
    onMouseDown,
    onKeyDown: (e: KeyboardEvent<HTMLInputElement>) => {
      // Space ticks a checkbox via a synthetic click; remember shift for it.
      if (e.key === ' ') shiftHeld.current = e.shiftKey;
    },
  });

  /** Props for a whole-row selection target. Interactive children keep their own clicks. */
  const rowProps = (id: K) => {
    const disabled = latest.current.isDisabled?.(id) ?? false;
    return {
      tabIndex: 0,
      'aria-disabled': disabled || undefined,
      onMouseDown,
      onClick: (e: MouseEvent<HTMLElement>) => {
        if (isInteractiveClick(e.target, e.currentTarget)) return;
        // Dragging to copy a handle shouldn't tick the row.
        if (!e.shiftKey && hasTextSelection()) return;
        shiftHeld.current = false;
        toggle(id, !latest.current.isSelected(id), e.shiftKey);
      },
      onKeyDown: (e: KeyboardEvent<HTMLElement>) => {
        if (e.target !== e.currentTarget || e.key !== ' ') return;
        e.preventDefault();
        toggle(id, !latest.current.isSelected(id), e.shiftKey);
      },
    };
  };

  const selectable = isDisabled ? visible.filter((id) => !isDisabled(id)) : [...visible];
  const headState: CheckState = checkStateOf(selectable, isSelected);

  return {
    toggle,
    checkboxProps,
    rowProps,
    /** Mousedown handler for a container (e.g. a <label>) whose click reaches a checkbox. */
    onMouseDown,
    isSelected,
    /** Selectable ids on screen. */
    selectable,
    /** Header checkbox state over the selectable ids on screen. */
    headState,
    /** Header checkbox: select every selectable id on screen, or clear them if all are selected. */
    toggleAll: () => {
      anchor.current = null;
      if (selectable.length) onApply(selectable, headState !== 'all');
    },
    selectAll: () => selectable.length > 0 && onApply(selectable, true),
    clear: () => {
      anchor.current = null;
      const on = visible.filter(isSelected);
      if (on.length) onApply(on, false);
    },
  };
}

function noop(): void {}

export type RangeSelection<K extends string | number> = ReturnType<typeof useRangeSelection<K>>;
/** Spread on a row that is itself a selection target. */
export type RangeRowProps = ReturnType<RangeSelection<string>['rowProps']>;
/** Spread on a row's checkbox (or pass to <RowCheckbox>). */
export type RangeCheckboxProps = ReturnType<RangeSelection<string>['checkboxProps']>;

/** An `onApply` for selection kept in a `useState<Set>`. */
export function applyToSet<K>(setSelected: (next: SetStateAction<Set<K>>) => void) {
  return (ids: K[], checked: boolean) => setSelected((prev) => setMany(prev, ids, checked));
}

// ── "Tip: Shift-click to select a range" ──────────────────────────────────
// Shown next to a bulk bar until the person has made one range selection,
// anywhere; then it stays gone (per browser).

const HINT_KEY = 'spade.rangeSelectHintSeen';
const hintListeners = new Set<() => void>();
let hintSeenCache: boolean | null = null;

function readHintSeen(): boolean {
  if (hintSeenCache !== null) return hintSeenCache;
  try {
    hintSeenCache = window.localStorage.getItem(HINT_KEY) === '1';
  } catch {
    hintSeenCache = false;
  }
  return hintSeenCache;
}

export function markRangeHintSeen(): void {
  if (readHintSeen()) return;
  hintSeenCache = true;
  try {
    window.localStorage.setItem(HINT_KEY, '1');
  } catch {
    // Private mode: the tip just comes back next visit.
  }
  for (const l of hintListeners) l();
}

/** Test hook: forget that the tip was seen. */
export function resetRangeHint(): void {
  hintSeenCache = null;
  try {
    window.localStorage.removeItem(HINT_KEY);
  } catch {
    // ignore
  }
  for (const l of hintListeners) l();
}

export function useRangeHintSeen(): boolean {
  return useSyncExternalStore(
    (cb) => {
      hintListeners.add(cb);
      return () => hintListeners.delete(cb);
    },
    readHintSeen,
    () => true,
  );
}
